// src/pages/pedidos/EnderecoPedidoModal.js
import React, { useEffect, useRef, useState } from 'react';
import { FiMapPin } from 'react-icons/fi';
import Input from '../../components/input/Input';
import Select from '../../components/input/Select';
import ModalContainer from '../../components/modalForm/ModalContainer';
import ModalHeader from '../../components/modalForm/ModalHeader';
import ModalBody from '../../components/modalForm/ModalBody';
import ModalFooter from '../../components/modalForm/ModalFooter';
import ModalActions, { ModalActionButton } from '../../components/modalForm/ModalActions';
import { useConfigModulo } from '../../contexts/administrativo/ConfigModuloContext';
import { useTaxaEntregaBairro } from '../../contexts/pedidos/TaxaEntregaBairroContext';
import { Pedido } from '../../models/pedidos/Pedido';
import './EnderecoPedidoModal.css';

function EnderecoPedidoModal({ show, onClose, onApply, dados, errosIniciais = {}, headerColors, isApplying = false, erroAplicacao = '' }) {
    const { configModuloSel } = useConfigModulo();
    const { taxasEntregaBairro, showTaxaEntregaBairroCad } = useTaxaEntregaBairro();
    const usarTaxaEntregaBairro = !!configModuloSel?.taxaEntregaBairro;
    const [rascunho, setRascunho] = useState({});
    const [erros, setErros] = useState({});
    const dadosIniciaisRef = useRef({});
    const formularioRef = useRef(null);
    const focarErroRef = useRef(false);

    useEffect(() => {
        if (!show) return;
        dadosIniciaisRef.current = { ...dados };
        setRascunho({ ...dados });
        setErros(errosIniciais);
        focarErroRef.current = Object.values(errosIniciais).some(Boolean);
        // O rascunho é iniciado somente ao abrir, sem ser substituído pelo cadastro de bairro sobreposto.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    useEffect(() => {
        if (!show || !focarErroRef.current) return;
        const campo = formularioRef.current?.querySelector('[aria-invalid="true"]');
        if (!campo) return;
        focarErroRef.current = false;
        campo?.focus({ preventScroll: true });
        campo?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }, [erros, show]);

    const atualizarCampo = (campo, valor) => {
        setRascunho((anterior) => ({ ...anterior, [campo]: valor }));
        setErros((anterior) => ({ ...anterior, [campo]: '' }));
    };

    const selecionarBairro = (event) => {
        const id = Number(event.target.value) || null;
        const bairro = taxasEntregaBairro.find((taxa) => Number(taxa.id) === id);
        setRascunho((anterior) => ({ ...anterior, taxaEntregaBairroId: id, bairro: bairro?.descricao || '' }));
        setErros((anterior) => ({ ...anterior, bairro: '', taxaEntregaBairroId: '' }));
    };

    const aplicarEndereco = () => {
        if (isApplying) return;
        const novosErros = Pedido.validarEnderecoEntrega(rascunho, usarTaxaEntregaBairro);
        setErros(novosErros);
        if (Object.keys(novosErros).length) {
            focarErroRef.current = true;
            return;
        }
        onApply(Pedido.prepararEnderecoEntrega(rascunho, dadosIniciaisRef.current, usarTaxaEntregaBairro, taxasEntregaBairro));
    };

    return (
        <ModalContainer show={show} variant="cadastro" onHide={() => { if (!isApplying) onClose(); }} largura="500px" contentClassName="endereco-pedido-modal modal-operacional">
            <ModalHeader title="Endereço de entrega" icon={FiMapPin} onHide={onClose} closeDisabled={isApplying} headerColors={headerColors} />
            <ModalBody>
                <fieldset ref={formularioRef} className="endereco-pedido-form" disabled={isApplying} aria-busy={isApplying} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                    <p className="endereco-pedido-orientacao">Confira o endereço desta entrega. Para clientes já cadastrados, as alterações serão salvas no cadastro ao aplicar o endereço.</p>
                    <div className="endereco-pedido-rua-numero">
                        <Input label="Rua/Avenida" value={rascunho.logradouro || ''} placeholder="Rua/Avenida" onChange={(event) => atualizarCampo('logradouro', event.target.value)} size="100%" msgErro={erros.logradouro} />
                        <Input label="Número" value={rascunho.numero || ''} placeholder="Número" onChange={(event) => atualizarCampo('numero', event.target.value)} size="100%" msgErro={erros.numero} />
                    </div>
                    {usarTaxaEntregaBairro ? (
                        <Select
                            label="Bairro"
                            searchable
                            disabled={isApplying}
                            btnAdd={showTaxaEntregaBairroCad}
                            placeholder="Digite para buscar o bairro"
                            emptyMessage="Nenhum bairro encontrado"
                            value={rascunho.taxaEntregaBairroId || ''}
                            onChange={selecionarBairro}
                            options={[{ value: '', label: 'Selecione...' }, ...taxasEntregaBairro.map((taxa) => ({ value: taxa.id, label: taxa.descricao }))]}
                            size="100%"
                            msgErro={erros.taxaEntregaBairroId || erros.bairro}
                        />
                    ) : (
                        <Input label="Bairro" value={rascunho.bairro || ''} placeholder="Bairro" onChange={(event) => atualizarCampo('bairro', event.target.value)} size="100%" msgErro={erros.bairro} />
                    )}
                    <Input label="Complemento" value={rascunho.complemento || ''} placeholder="Apartamento, bloco, referência..." onChange={(event) => atualizarCampo('complemento', event.target.value)} size="100%" />
                </fieldset>
                {erroAplicacao && <p role="alert" className="text-danger mt-2 mb-0">{erroAplicacao}</p>}
            </ModalBody>
            <ModalFooter>
                <ModalActions>
                    <ModalActionButton action="close" onClick={onClose} disabled={isApplying}>Cancelar</ModalActionButton>
                    <ModalActionButton action="apply" onClick={aplicarEndereco} disabled={isApplying}>{isApplying ? 'Salvando endereço...' : 'Aplicar endereço'}</ModalActionButton>
                </ModalActions>
            </ModalFooter>
        </ModalContainer>
    );
}

export default EnderecoPedidoModal;
